import { Turnstile } from "@marsidev/react-turnstile";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useState, type FC } from "react";
import { useForm } from "react-hook-form";
import FormTextField, {
  emailValidationPattern,
  phoneNumberValidationPattern,
} from "../components/formComponents/FormTextField";
import { useSendB2bRequest } from "./useSendB2bRequest";

interface Props {
  readonly openRequestDialog: boolean;
  readonly setOpenRequestDialog: (value: boolean) => void;
}

interface B2bRequestFormState {
  name: string;
  company: string;
  nip: string;
  email: string;
  phone: string;
}

const B2bOptionRequestDialog: FC<Props> = ({ openRequestDialog, setOpenRequestDialog }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { control, handleSubmit } = useForm<B2bRequestFormState>({
    defaultValues: { name: "", company: "", nip: "", email: "", phone: "" },
  });
  const { sendB2bRequest } = useSendB2bRequest();

  const [token, setToken] = useState("");

  const handleSubmitRequest = async (data: B2bRequestFormState) => {
    try {
      await sendB2bRequest({ ...data, token });
      enqueueSnackbar("Wniosek został wysłany. Skontaktujemy się z Tobą wkrótce.", { variant: "success" });
      setOpenRequestDialog(false);
    } catch (error) {
      console.error("Error submitting B2B request:", error);
      enqueueSnackbar("Wystąpił błąd podczas wysyłania wniosku. Spróbuj ponownie później.", { variant: "error" });
    }
  };

  return (
    <Dialog
      open={openRequestDialog}
      onClose={() => setOpenRequestDialog(false)}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
          },
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: "bold" }}>
          Wniosek o dostęp
        </Typography>
        <IconButton onClick={() => setOpenRequestDialog(false)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 3 }}>
          Platforma B2B PrzezMorze.pl jest dostępna wyłącznie dla zweryfikowanych partnerów biznesowych. Wypełnij
          poniższy formularz, a nasz przedstawiciel skontaktuje się z Tobą w celu nadania dostępu.
        </DialogContentText>
        <Turnstile siteKey={"0x4AAAAAADp4XxJh3b4L_JEI"} onSuccess={setToken} />

        <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <FormTextField
            control={control}
            name="name"
            label="Imię i nazwisko"
            fullWidth
            variant="outlined"
            required
            rules={{ required: true }}
            InputProps={{
              sx: { borderRadius: 2 },
            }}
          />
          <FormTextField
            control={control}
            name="company"
            label="Nazwa firmy"
            fullWidth
            variant="outlined"
            required
            rules={{ required: true }}
            InputProps={{
              sx: { borderRadius: 2 },
            }}
          />
          <FormTextField
            control={control}
            name="nip"
            label="NIP"
            fullWidth
            variant="outlined"
            required
            rules={{ required: true }}
            InputProps={{
              sx: { borderRadius: 2 },
            }}
          />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormTextField
                control={control}
                name="email"
                label="Adres e-mail"
                type="email"
                fullWidth
                required
                variant="outlined"
                rules={{ required: true, pattern: emailValidationPattern }}
                InputProps={{
                  sx: { borderRadius: 2 },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormTextField
                control={control}
                name="phone"
                label="Telefon kontaktowy"
                type="phone"
                fullWidth
                required
                variant="outlined"
                rules={{ required: true, pattern: phoneNumberValidationPattern }}
                InputProps={{
                  sx: { borderRadius: 2 },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button onClick={() => setOpenRequestDialog(false)} color="inherit" sx={{ fontWeight: 600 }}>
          Anuluj
        </Button>
        <Button
          onClick={handleSubmit(handleSubmitRequest)}
          variant="contained"
          color="primary"
          sx={{ px: 4, borderRadius: 2, fontWeight: 600 }}
        >
          Wyślij wniosek
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default B2bOptionRequestDialog;
